
/**
 * Middleware to persist API request details to browser storage.
 */
import { datadogRum } from '@datadog/browser-rum';

import { startBackgroundRetry, stopBackgroundRetry } from '../api/apiCallActions';
import { isAudioSavingRequest } from '../api/apiUtils';
import { makeAPIRequestRetryErrors, makeAPIRequest } from '../api/apiActions';
import {
    SYNC_PERSISTED_RECORDING,
    SAVE_RESULT_TO_STORAGE,
    REMOVE_RESULT_FROM_STORAGE,
    SAVE_CONTENT_TO_STORAGE,
} from './persistanceActions';
import { EXECUTE_API_CALL_FAILED_EFFECT, EXECUTE_API_CALL_SUCCEEDED_EFFECT } from '../api/trcoActions';
import { SET_USER, SET_USER_FROM_STORAGE, setUser } from '../user/userActions';
import { RECORDINGS_STORE_NAME, INFO_DATA, RESULTS_STORE_NAME, CONTENT_STORE_NAME } from './constants';
import { getPersistenceStorages, getStudentResultKey } from './storage';

const createRequestPersistenceMiddleware = () => {
    const persistenceStorages = getPersistenceStorages();

    return ({dispatch}) => next => async action => {
        let nextAction;
        try {
            switch (action.type) {
            case EXECUTE_API_CALL_FAILED_EFFECT: {
                const { payload: { prevApiRequest, resultId } } = action;
                nextAction = next(action);
                if (isAudioSavingRequest(prevApiRequest)) {
                    await persistenceStorages[RECORDINGS_STORE_NAME].setItem(resultId, {
                        endpoint: prevApiRequest.endpoint,
                        requestConfig: prevApiRequest.requestConfig,
                    });
                }
                break;
            }
            case EXECUTE_API_CALL_SUCCEEDED_EFFECT: {
                const { payload: { prevApiRequest, resultId } } = action;
                nextAction = next(action);
                if (isAudioSavingRequest(prevApiRequest)) {
                    await persistenceStorages[RECORDINGS_STORE_NAME].removeItem(resultId);
                }
                break;
            }
            case SYNC_PERSISTED_RECORDING: {
                const requestData = await persistenceStorages[RECORDINGS_STORE_NAME].getItem(action.resultId);
                if (requestData && requestData.endpoint && requestData.requestConfig) {
                    if (action.shouldRetry) {
                        dispatch(startBackgroundRetry());
                        return dispatch(
                            makeAPIRequestRetryErrors(
                                requestData.endpoint,
                                requestData.requestConfig,
                            ),
                        ).finally(() => {
                            dispatch(stopBackgroundRetry());
                        });
                    }
                    return dispatch(
                        makeAPIRequest(requestData.endpoint, requestData.requestConfig),
                    );
                }
                break;
            }
            case SET_USER: {
                nextAction = next(action);
                window.sessionStorage.setItem(INFO_DATA, JSON.stringify(action));
                break;
            }
            case SET_USER_FROM_STORAGE: {
                nextAction = next(action);
                const userData = window.sessionStorage.getItem(INFO_DATA);
                if (userData) {
                    const { userId, userName, userType, aquaId } =  JSON.parse(userData);
                    dispatch(setUser(userId, userName, userType, aquaId));
                }
                break;
            }
            case SAVE_RESULT_TO_STORAGE: {
                const { studentUid, measure, postData } = action;
                nextAction = next(action);
                await persistenceStorages[RESULTS_STORE_NAME].setItem(getStudentResultKey(studentUid, measure), JSON.stringify(postData));
                break;
            }
            case REMOVE_RESULT_FROM_STORAGE: {
                const { studentUid, measure } = action;
                nextAction = next(action);
                const resultKey = getStudentResultKey(studentUid, measure);
                const resultExist = await persistenceStorages[RESULTS_STORE_NAME].getItem(resultKey) !== null;
                if (resultExist) {
                    await persistenceStorages[RESULTS_STORE_NAME].removeItem(resultKey);
                }
                break;
            }
            case SAVE_CONTENT_TO_STORAGE: {
                const { endpoint, data } = action;
                nextAction = next(action);
                await persistenceStorages[CONTENT_STORE_NAME].setItem(endpoint, data);
                break;
            }
            default:
                nextAction = next(action);
                break;
            }
        } catch (error) {
            if (datadogRum) {
                datadogRum.addError(error);
            }
        }
        return nextAction;
    };
};

export const requestPersistenceMiddleware = createRequestPersistenceMiddleware();

export default requestPersistenceMiddleware;
